/**
 * Modelle für den Stammkunden-Bereich (Aufträge + Personen-Einsatzplanung).
 * Die String-Werte entsprechen den Backend-Enum-Namen (JsonStringEnumConverter).
 */

export type AuftragStatus =
  | 'New'
  | 'Planned'
  | 'InProgress'
  | 'Recorded'
  | 'Approved'
  | 'Completed'
  | 'Priced'
  | 'Billed';

/** Deutsche Anzeige-Labels für den Auftragsstatus. */
export const AUFTRAG_STATUS_LABELS: Record<AuftragStatus, string> = {
  New: 'Neu',
  Planned: 'Geplant',
  InProgress: 'In Arbeit',
  Recorded: 'Erfasst',
  Approved: 'Freigegeben',
  Completed: 'Abgeschlossen',
  Priced: 'Bepreist',
  Billed: 'Abgerechnet'
};

export const AUFTRAG_STATUS_ORDER: AuftragStatus[] = [
  'New', 'Planned', 'InProgress', 'Recorded', 'Approved', 'Completed', 'Priced', 'Billed'
];

export interface Auftrag {
  id: number;
  customerId: number;
  customerName?: string;
  /**
   * true, wenn der Auftrag zum konfigurierten Takeda-Kunden gehört
   * ("TakedaFeatures:CustomerId"). Steuert nur die Anzeige der Takeda-Ansichten.
   */
  isTakeda?: boolean;
  title: string;
  description?: string;
  orderNumber?: string;
  receivedDate?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  status: AuftragStatus;
  location?: string;
  /** Projektnummer aus den Takeda-Stammdaten (Schnappschuss des Nummerntexts). */
  projektNummer?: string | null;
  primaryWarehouseId?: number | null;
  primaryWarehouseName?: string | null;
  assignmentCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface AuftragCreate {
  customerId: number;
  title: string;
  description?: string;
  orderNumber?: string;
  receivedDate?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  location?: string;
  /** Muss zu einer aktiven Projektnummer des Stammkunden passen; leer = keine. */
  projektNummer?: string | null;
  primaryWarehouseId?: number | null;
  status?: AuftragStatus;
}

/** Einsatzort / Bau eines Stammkunden – Stammdaten für die Auswahl am Auftrag. */
export interface TakedaEinsatzort {
  id: number;
  customerId: number;
  name: string;
  beschreibung?: string | null;
  isActive: boolean;
  sortOrder: number;
}

export interface TakedaEinsatzortSave {
  name: string;
  beschreibung?: string | null;
  isActive: boolean;
  sortOrder: number;
}

/** Projektnummer eines Stammkunden – ein Kunde kann mehrere haben. */
export interface TakedaProjektnummer {
  id: number;
  customerId: number;
  nummer: string;
  bezeichnung?: string | null;
  isActive: boolean;
}

export interface TakedaProjektnummerSave {
  nummer: string;
  bezeichnung?: string | null;
  isActive: boolean;
}

/**
 * Vorschlag für die einmalige Übernahme aus "Anlagen & Verteiler".
 * `vorhandenAlsEinsatzort` markiert, was schon angelegt ist.
 */
export interface TakedaEinsatzortVorschlag {
  equipmentId: number;
  name: string;
  location?: string | null;
  vorhandenAlsEinsatzort: boolean;
}

/** Rolle eines Benutzers innerhalb eines Stammkunden. */
export type StammkundeRole = 'Bauleiter' | 'Vorarbeiter';

export interface StammkundeMember {
  userId: string;
  userName?: string | null;
  email?: string | null;
  role: StammkundeRole;
}

export interface MyStammkundeMembership {
  customerId: number;
  role: StammkundeRole;
}

export interface WarehouseRef {
  id: number;
  name: string;
}

export interface CustomerRef {
  id: number;
  name: string;
}

/** Vom Vorarbeiter selbst angelegter Spontan-Einsatz. */
export interface SpontanEinsatzCreate {
  customerId: number;
  auftragId?: number | null;
  title?: string;
  start: string;
  end: string;
  location?: string;
  notes?: string;
  participantUserIds?: string[];
  workerIds?: number[];
}

export interface PlannableUser {
  id: string;
  userName?: string | null;
  email?: string | null;
}

/** Arbeiter im Team eines Stammkunden – OHNE App-Login ("Personal"). */
export interface StammkundeWorker {
  id: number;
  name: string;
  phone?: string | null;
}

/** Status einer Materialposition (analog zu den Stunden). */
export type MaterialStatus = 'Submitted' | 'Approved' | 'Locked';

export const MATERIAL_STATUS_LABELS: Record<MaterialStatus, string> = {
  Submitted: 'Erfasst',
  Approved: 'Freigegeben',
  Locked: 'Abgerechnet'
};

/** Material eines Einsatzes (reine Dokumentation, ohne Preise). */
export interface EinsatzMaterial {
  id?: number | null;
  name: string;
  quantity: number;
  unit: string;
  status?: MaterialStatus;
}

/** Kennzahlen eines Projekts (Auftrag) für die Bauleiter-Gesamtübersicht. */
export interface AuftragOverview {
  auftragId: number;
  title: string;
  orderNumber?: string | null;
  customerId: number;
  customerName?: string | null;
  status: AuftragStatus;
  totalHours: number;
  submittedHours: number;
  approvedHours: number;
  submittedHourCount: number;
  hourEntryCount: number;
  materialTotalCount: number;
  materialSubmittedCount: number;
  assignmentCount: number;
  lastActivityUtc?: string | null;
}

/** Stunden-Auswertung je Person (wer wie viel gearbeitet hat). */
export interface PersonHours {
  personName: string;
  workerId?: number | null;
  personUserId?: string | null;
  totalHours: number;
  submittedHours: number;
  approvedHours: number;
  entryCount: number;
}

/** Status eines Stunden-Eintrags. */
export type TimeEntryStatus = 'Submitted' | 'Approved' | 'Locked';

export const TIME_ENTRY_STATUS_LABELS: Record<TimeEntryStatus, string> = {
  Submitted: 'Erfasst',
  Approved: 'Freigegeben',
  Locked: 'Abgerechnet'
};

/** Erfasste Arbeitsstunden (ohne Preise). */
export interface ZeitEintrag {
  id: number;
  auftragId: number;
  assignmentId?: number | null;
  workDate: string;
  hours: number;
  personUserId?: string | null;
  workerId?: number | null;
  personName: string;
  note?: string | null;
  status: TimeEntryStatus;
  recordedByUserId: string;
  createdAtUtc: string;
  /** Gesetzt, wenn der Eintrag aus einem abgeschlossenen Takeda-Tagesbericht stammt. */
  takedaArbeitsbereichId?: number | null;
}

export interface ZeitEintragCreate {
  assignmentId?: number | null;
  workDate: string;
  hours: number;
  personUserId?: string | null;
  workerId?: number | null;
  personName?: string | null;
  note?: string | null;
}

/** Einplanung (ResourceAssignment) im Auftrags-Kontext. */
/**
 * Geplante LV-Position eines Einsatzes – die SOLL-Menge.
 *
 * Plan und Ist sind zwei getrennte Werte zur selben LV-Position: diese Zeile
 * bleibt bei der Übernahme in den Tagesbericht unverändert bestehen. Sie
 * erreicht die Materialprojektion nie und ist damit nicht abrechnungswirksam.
 */
export interface TakedaEinsatzPlanLvPosition {
  id: number;
  lvPositionId?: number | null;
  sortOrder: number;
  /** null = Position vorgemerkt, Menge noch offen. */
  planMenge?: number | null;
  nummer: string;
  stichwort?: string | null;
  bezeichnung?: string | null;
  einheit?: string | null;
}

/** Geplante LV-Position beim Speichern: nur Referenz und Planmenge. */
export interface TakedaEinsatzPlanLvPositionSave {
  lvPositionId: number;
  planMenge?: number | null;
}

export interface AuftragAssignment {
  id: number;
  assignedUserId: string;
  assignedUserName?: string | null;
  assignedUserEmail?: string | null;
  participantUserIds: string[];
  participantNames: string[];
  workerIds: number[];
  workerNames: string[];
  materials: EinsatzMaterial[];
  /**
   * Geplante LV-Positionen (SOLL, nur Takeda). Bewusst getrennt von `materials`:
   * Planung ist kein Verbrauch und nicht abrechnungswirksam.
   */
  planLvPositionen?: TakedaEinsatzPlanLvPosition[];
  /**
   * true = Materialplanung gesperrt, weil alle Tagesberichte dieses Einsatzes
   * abgeschlossen sind. null/undefined = vom Endpunkt nicht ermittelt; nur `true`
   * sperrt, damit eine fehlende Angabe nie als Freigabe gilt.
   */
  materialplanungGesperrt?: boolean | null;
  title: string;
  start: string;
  end: string;
  location?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  /** true = Takeda-Einsatz: Tagesbericht statt separater Stunden-/Material-Erfassung. */
  takedaRegieEnabled?: boolean;
  auftragId?: number | null;
  auftragTitle?: string | null;
  /** SES-/IND-Nummer des Auftrags (Auftraege.OrderNumber), nur zur Anzeige. */
  auftragOrderNumber?: string | null;
  notes?: string | null;
  workStatus: string;
  inviteSent: boolean;
  inviteSentAtUtc?: string | null;
}

export interface AuftragAssignmentCreate {
  assignedUserId: string;
  participantUserIds?: string[];
  workerIds?: number[];
  /** undefined = Material unverändert lassen; sonst Liste vollständig ersetzen. */
  materials?: EinsatzMaterial[];
  /**
   * Geplante LV-Positionen (SOLL, nur Takeda). Gleiche Semantik wie `materials`:
   * undefined = unverändert lassen, sonst vollständig ersetzen.
   */
  planLvPositionen?: TakedaEinsatzPlanLvPositionSave[];
  title?: string;
  start: string;
  end: string;
  location?: string;
  notes?: string;
  sendInvite?: boolean;
  workStatus?: string;
}

// ── Takeda-Tagesbericht (nur für den Stammkunden Takeda) ──

export type TakedaTagesberichtStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'Locked';

export const TAKEDA_TAGESBERICHT_STATUS_LABELS: Record<TakedaTagesberichtStatus, string> = {
  Draft: 'Entwurf',
  Submitted: 'Erfasst',
  Approved: 'Freigegeben',
  Locked: 'Abgerechnet'
};

export type TakedaReferenzTyp =
  | 'Ses'
  | 'Angebot';

export const TAKEDA_REFERENZ_TYP_LABELS: Record<TakedaReferenzTyp, string> = {
  Ses: 'SES',
  Angebot: 'Angebot'
};

/** Personenstunden innerhalb eines Arbeitsbereichs (TakedaArbeitszeitDto). */
export interface TakedaArbeitszeit {
  id: number;
  personUserId?: string | null;
  workerId?: number | null;
  personName: string;
  hours: number;
}

/** Arbeitsbereich eines Tagesberichts (TakedaArbeitsbereichDto). */
export interface TakedaArbeitsbereich {
  id: number;
  sortOrder: number;
  referenzTyp: TakedaReferenzTyp;
  referenzNummer: string;
  ort?: string | null;
  beschreibung: string;
  materialText?: string | null;

  lvPositionen: TakedaArbeitsbereichLvPosition[];

  /** Frei erfasstes Material ohne LV-Bezug. */
  freieMaterialien: TakedaArbeitsbereichFreiesMaterial[];

  arbeitszeiten: TakedaArbeitszeit[];
}

/** Tagesbericht je Einsatz und Tag (TakedaTagesberichtDto). */
export interface TakedaTagesbericht {
  id: number;
  auftragId: number;
  assignmentId?: number | null;
  workDate: string;
  /** SES-/IND-Nummer des Projekts (Auftraege.OrderNumber). */
  orderNumber?: string | null;
  /** "SES", "IND" oder neutral "SES / IND". */
  referenzLabel?: string | null;
  status: TakedaTagesberichtStatus;
  createdByUserId: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  submittedAtUtc?: string | null;
  arbeitsbereiche: TakedaArbeitsbereich[];
}

export interface TakedaArbeitszeitSave {
  personUserId?: string | null;
  workerId?: number | null;
  personName?: string | null;
  hours: number;
}

export interface TakedaArbeitsbereichSave {
  referenzTyp: TakedaReferenzTyp;
  referenzNummer?: string | null;
  ort?: string | null;
  beschreibung?: string | null;
  materialText?: string | null;

  lvPositionen: TakedaArbeitsbereichLvPositionSave[];

  freieMaterialien: TakedaArbeitsbereichFreiesMaterialSave[];

  arbeitszeiten: TakedaArbeitszeitSave[];
}

export interface TakedaTagesberichtSave {
  workDate: string;
  arbeitsbereiche: TakedaArbeitsbereichSave[];
}

// ── Takeda-Projektansicht (nur lesen, bewusst ohne Preise) ──

/** Stunden einer Person; Identität über personUserId bzw. workerId, nie über den Namen. */
export interface TakedaProjektPersonStunden {
  personUserId?: string | null;
  workerId?: number | null;
  personName: string;
  hours: number;
}

export interface TakedaProjektEinsatzStunden {
  assignmentId?: number | null;
  assignmentTitle?: string | null;
  workDate: string;
  hours: number;
  personen: TakedaProjektPersonStunden[];
}

/** Verwendete LV-Position – ohne jedes Preisfeld. */
export interface TakedaProjektLvMaterial {
  /**
   * Id der Snapshot-Zeile. In der Einsatzansicht immer gesetzt, in "Gesamt" nur,
   * wenn die Zeile aus genau einer Erfassung stammt (sonst 0).
   */
  id: number;
  /** Aus wie vielen Erfassungen die Zeile besteht (Einsatzansicht: immer 1). */
  quellenAnzahl: number;
  lvPositionId?: number | null;
  nummer: string;
  bezeichnung?: string | null;
  stichwort?: string | null;
  menge: number;
  einheit?: string | null;
}

/** Frei erfasstes Material; jede gespeicherte Zeile bleibt eine Zeile. */
export interface TakedaProjektFreiesMaterial {
  /** Id der Takeda-Zeile; 0 bei noch nicht uebernommenem Altbestand. */
  id: number;
  /** 'Frei' = Takeda-Zeile, 'Legacy' = Altbestand aus ResourceAssignmentMaterials. */
  art: 'Frei' | 'Legacy';
  /** Bei art 'Legacy': Schluessel fuer die Legacy-Endpunkte. */
  legacyMaterialId?: number | null;
  bezeichnung: string;
  menge: number;
  einheit?: string | null;
  assignmentId?: number | null;
  workDate?: string | null;
  referenzTyp?: string | null;
  referenzNummer?: string | null;
}

export interface TakedaProjektArbeitsbereichMaterial {
  /** 0 = noch kein Takeda-Arbeitsbereich; neues Material laeuft ueber assignmentId. */
  arbeitsbereichId: number;
  assignmentId?: number | null;
  referenzTyp: string;
  referenzNummer: string;
  ort?: string | null;
  beschreibung?: string | null;
  lvPositionen: TakedaProjektLvMaterial[];
  freieMaterialien: TakedaProjektFreiesMaterial[];
}

export interface TakedaProjektEinsatzMaterial {
  assignmentId?: number | null;
  assignmentTitle?: string | null;
  workDate: string;
  arbeitsbereiche: TakedaProjektArbeitsbereichMaterial[];
}

/** Aggregierte Stunden- und Materialdaten eines Takeda-Auftrags. */
export interface TakedaProjektDaten {
  auftragId: number;
  stundenGesamt: TakedaProjektPersonStunden[];
  stundenNachEinsatz: TakedaProjektEinsatzStunden[];
  lvMaterialGesamt: TakedaProjektLvMaterial[];
  freiesMaterialGesamt: TakedaProjektFreiesMaterial[];
  materialNachEinsatz: TakedaProjektEinsatzMaterial[];
}

// ── Takeda-Bepreisung (nur über den geschützten Pricing-Endpunkt) ──

/** Eine bepreisbare Materialzeile – LV-Position oder freies Material. */
export interface TakedaPricingMaterialZeile {
  art: 'Lv' | 'Frei' | 'Legacy';
  lvPositionId?: number | null;
  /** Gesetzt bei freiem Material – Ziel des Preis-Endpunkts. */
  freiesMaterialId?: number | null;
  /** Bei art 'Legacy': Ziel des Legacy-Preis-Endpunkts; noch ohne Preis. */
  legacyMaterialId?: number | null;
  nummer: string;
  bezeichnung?: string | null;
  stichwort?: string | null;
  menge: number;
  einheit?: string | null;
  /** Schnappschuss je Einheit; null = noch nicht bepreist. */
  preisProEinheit?: number | null;
  gesamt?: number | null;
  referenzTyp?: string | null;
  referenzNummer?: string | null;
}

export interface TakedaPricingArbeitsbereichMaterial {
  arbeitsbereichId: number;
  referenzTyp: string;
  referenzNummer: string;
  ort?: string | null;
  zeilen: TakedaPricingMaterialZeile[];
}

export interface TakedaPricingEinsatzMaterial {
  assignmentId?: number | null;
  assignmentTitle?: string | null;
  workDate: string;
  arbeitsbereiche: TakedaPricingArbeitsbereichMaterial[];
}

/** Abrechnungszeile für Stunden; Betrag aus dem eingefrorenen Satz. */
export interface TakedaPricingStundenZeile {
  personUserId?: string | null;
  workerId?: number | null;
  personName: string;
  rateLabel?: string | null;
  hours: number;
  hourlyRate?: number | null;
  gesamt?: number | null;
}

export interface TakedaPricingEinsatzStunden {
  assignmentId?: number | null;
  assignmentTitle?: string | null;
  workDate: string;
  hours: number;
  gesamt?: number | null;
  personen: TakedaPricingStundenZeile[];
}

export interface TakedaPricing {
  auftragId: number;
  status: AuftragStatus;
  /** Ab Completed. */
  materialPreiseSichtbar: boolean;
  /** Ab Priced. */
  stundenPreiseSichtbar: boolean;
  /** Nur im Status Completed. Ab Priced sind Mengen und Preise readonly. */
  freiesMaterialBearbeitbar: boolean;
  materialGesamt: TakedaPricingMaterialZeile[];
  materialNachEinsatz: TakedaPricingEinsatzMaterial[];
  stundenGesamt: TakedaPricingStundenZeile[];
  stundenNachEinsatz: TakedaPricingEinsatzStunden[];
  materialSumme?: number | null;
  stundenSumme?: number | null;
  gesamtSumme?: number | null;
  /** > 0: Auftrag wurde vor Einführung der Snapshots bepreist – Beträge unvollständig. */
  stundenOhneSnapshot: number;
  /** > 0: Material ohne Preis, inkl. noch nicht uebernommenem Altbestand. */
  materialOhnePreis: number;
}

// ── Takeda-Abrechnung (Export) ──

/**
 * Abrechnungsgrundlage eines Takeda-Auftrags – alle Beträge kommen fertig
 * gerechnet vom Server (TakedaAbrechnungService). Das PDF setzt diese Werte nur
 * noch, es wird im Browser bewusst NICHTS nachgerechnet.
 */
export interface TakedaAbrechnungStunde {
  personName: string;
  rateLabel?: string | null;
  hours: number;
  hourlyRate?: number | null;
  betrag?: number | null;
}

export interface TakedaAbrechnungMaterial {
  art: 'Lv' | 'Frei' | 'Legacy';
  lvNummer?: string | null;
  bezeichnung: string;
  stichwort?: string | null;
  menge: number;
  einheit?: string | null;
  preis?: number | null;
  betrag?: number | null;
}

export interface TakedaAbrechnungBlock {
  nummer: string;
  arbeitsbereichId: number;
  assignmentId?: number | null;
  assignmentTitle?: string | null;
  workDate: string;
  ort?: string | null;
  abweichendeReferenz?: string | null;
  beschreibung?: string | null;
  stunden: TakedaAbrechnungStunde[];
  material: TakedaAbrechnungMaterial[];
  stundenSumme: number;
  materialSumme: number;
  summe: number;
}

export interface TakedaAbrechnung {
  auftragId: number;
  customerName: string;
  titel: string;
  orderNumber?: string | null;
  referenzLabel: string;
  ort?: string | null;
  status: AuftragStatus;
  statusLabel: string;
  von?: string | null;
  bis?: string | null;
  bloecke: TakedaAbrechnungBlock[];
  stundenSumme: number;
  materialSumme: number;
  gesamtSumme: number;
}

/**
 * Einsatz-Auswahl für den Excel-Export „Nach Einsätzen“ (nur Takeda).
 * `einsatzId` ist die AssignmentId des Einsatzes; 0 steht für Positionen ohne
 * Einsatzbezug. Die Liste kommt vom Server aus derselben Abrechnung, die auch
 * exportiert wird.
 */
export interface TakedaExportEinsatzOption {
  einsatzId: number;
  nummer: string;
  titel: string;
  von: string;
  bis: string;
  anzahlBloecke: number;
  summe: number;
}

// ── Takeda-Leistungsverzeichnis ──

/** Kopf eines importierten Takeda-Leistungsverzeichnisses. */
export interface TakedaLeistungsverzeichnis {
  id: number;
  customerId: number;
  name: string;
  fileName: string;
  isActive: boolean;
  importedAtUtc: string;
  importedByUserId?: string | null;
  positionCount: number;
}

/** Einzelne Zeile/Position aus einem Takeda-Leistungsverzeichnis. */
export interface TakedaLvPosition {
  id: number;
  leistungsverzeichnisId: number;
  rowNumber: number;
  nummer: string;
  stichwort?: string | null;
  /** Frei pflegbare Such-/Klartextbezeichnung, z.B. "LED-Leuchte Reinraum". */
  bezeichnung?: string | null;
  typ?: string | null;
  lvMenge?: number | null;
  einheit?: string | null;
  lohn?: number | null;
  sonstiges?: number | null;
  einheitspreis?: number | null;
  gesamtbetrag?: number | null;
  markierungskennzeichen?: string | null;
  lvNotiz?: string | null;
}


/** Werte zum manuellen Anlegen oder Bearbeiten einer LV-Zeile. */
export interface TakedaLvPositionSave {
  nummer: string;
  stichwort?: string | null;
  /** Frei pflegbare Such-/Klartextbezeichnung; darf leer bleiben. */
  bezeichnung?: string | null;
  typ?: string | null;
  lvMenge?: number | null;
  einheit?: string | null;
  lohn?: number | null;
  sonstiges?: number | null;
  einheitspreis?: number | null;
  gesamtbetrag?: number | null;
  markierungskennzeichen?: string | null;
  lvNotiz?: string | null;
}

export interface TakedaArbeitsbereichLvPositionSave {
  lvPositionId: number;
  menge: number;
}

/**
 * Verwendete LV-Position im Tagesbericht.
 *
 * Enthält bewusst KEINE Preisfelder: der Tagesbericht ist eine Erfassungsansicht,
 * der Server liefert Lohn/Sonstiges/Einheitspreis dort gar nicht erst aus. Die Werte
 * bleiben serverseitig im Snapshot für die spätere Bepreisung erhalten.
 */
export interface TakedaArbeitsbereichLvPosition {
  id: number;
  lvPositionId?: number | null;
  sortOrder: number;
  menge: number;

  nummer: string;
  stichwort?: string | null;
  /** Schnappschuss der Klartextbezeichnung der LV-Position. */
  bezeichnung?: string | null;
  typ?: string | null;
  einheit?: string | null;
}

/**
 * Suchtreffer der LV-Auswahl im Tagesbericht
 * (GET .../tagesbericht/lv-positionen). Ohne jedes Preisfeld.
 */
export interface TakedaTagesberichtLvPosition {
  id: number;
  nummer: string;
  stichwort?: string | null;
  bezeichnung?: string | null;
  typ?: string | null;
  einheit?: string | null;
}

/** Frei erfasstes Material eines Arbeitsbereichs – ohne Preis (siehe Bepreisung). */
export interface TakedaArbeitsbereichFreiesMaterial {
  id: number;
  sortOrder: number;
  bezeichnung: string;
  menge: number;
  einheit?: string | null;
}

/**
 * Freies Material beim Speichern. Die `id` bestehender Zeilen MUSS mitgesendet
 * werden, damit ein bereits vom Bauleiter gepflegter Preis serverseitig erhalten
 * bleibt. Einen Preis sendet der Tagesbericht bewusst nie.
 */
export interface TakedaArbeitsbereichFreiesMaterialSave {
  id?: number | null;
  bezeichnung: string;
  menge: number;
  einheit?: string | null;
}
